import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useNavigate } from 'react-router'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { cartItemsAtom, cartTotalAtom, updateQuantityAtom, removeFromCartAtom, currencyAtom } from '@/core/store/cart.store'
import { useTranslate } from '@repo/unicity'
import { UInput } from '@/core/components/ui'
import { Fragment, useEffect } from 'react'
import Divider from '@mui/material/Divider'
import { formatPrice } from '../home'

const MinusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
        <path d="M9 7H6.85714H6.14286H4V6H6.14286H6.85714H9V7Z" fill="#5A8FC3" />
    </svg>
)

const PlusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
        <path d="M10.2913 7.04134H7.04134V10.2913H5.95801V7.04134H2.70801V5.95801H5.95801V2.70801H7.04134V5.95801H10.2913V7.04134Z" fill="#5A8FC3" />
    </svg>
)

const Cart = () => {
    const navigate = useNavigate()
    const { translate } = useTranslate()
    const [cartItems] = useAtom(cartItemsAtom)
    const cartTotal = useAtomValue(cartTotalAtom)
    const updateQuantity = useSetAtom(updateQuantityAtom)
    const removeFromCart = useSetAtom(removeFromCartAtom)
    const currency = useAtomValue(currencyAtom)

    // Calculate total quantity
    const totalQty = cartItems.reduce((sum, item) => sum + item.quantity, 0)

    // Check if cart is empty and redirect to home
    useEffect(() => {
        if (cartItems.length === 0) {
            navigate('/')
        }
    }, [cartItems, navigate])

    return (
        <Stack sx={{ flex: 1 }} p={'48px 24px'} gap={3} bgcolor={'secondary.main'}>
            {/* Header */}
            <Stack gap={'12px'}>
                <Typography variant="h2" fontWeight={700} color="primary.main" textAlign="center" textTransform="uppercase">
                    {translate('your_cart')}
                </Typography>
                <Typography variant="body1" color="primary.main" textAlign="center">
                    {translate('total')} ({totalQty} {translate(totalQty > 1 ? 'items' : 'item')})
                </Typography>
            </Stack>

            {/* Cart Items */}
            <Stack
                sx={{
                    p: 2,
                    border: '1px solid',
                    borderColor: 'rgba(204, 222, 239, 1)',
                    borderRadius: 2
                }}>
                {cartItems.map((item, index) => (
                    <Fragment key={item.ticket.id}>
                        <Stack direction={'row'} justifyContent="space-between" alignItems="flex-start">
                            <Stack sx={{ flex: 1 }} gap={1}>
                                <Typography variant="body2" fontWeight={700} color="primary.main">
                                    {item.ticket.category}
                                </Typography>
                                <Typography variant="caption2" fontWeight={400} color="warning.main">
                                    {item.ticket.name}
                                </Typography>
                            </Stack>
                            <Stack gap={1}>
                                <Stack justifyContent={'center'}>
                                    <Typography variant="body2" color="primary.main" fontWeight={700} textAlign={'right'}>
                                        {formatPrice(item.ticket.price, currency, true)}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    {item.locked ? (
                                        // USHOP-2914: early-bird for an eligible customer — fixed at 1, no stepper
                                        <Stack
                                            sx={{
                                                bgcolor: 'background2.light',
                                                borderRadius: '8px',
                                                height: '32px',
                                                width: '61px',
                                                boxSizing: 'border-box',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                            <Typography variant="body2" color="primary.main" fontWeight={600}>
                                                {item.quantity}
                                            </Typography>
                                        </Stack>
                                    ) : (
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            sx={{
                                                bgcolor: 'background2.light',
                                                borderRadius: '8px',
                                                height: '32px',
                                                width: '61px',
                                                boxSizing: 'border-box'
                                            }}>
                                            <Stack
                                                onClick={() => updateQuantity(item.ticket.id, item.quantity - 1)}
                                                sx={{
                                                    height: '32px',
                                                    width: '21px',
                                                    padding: 0,
                                                    boxSizing: 'border-box',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                <MinusIcon />
                                            </Stack>
                                            <Stack
                                                sx={{
                                                    width: '19px',
                                                    height: '32px'
                                                }}>
                                                <UInput
                                                    value={item.quantity}
                                                    onChange={e => {
                                                        const value = parseInt(e.target.value)

                                                        if (!isNaN(value) && value >= 0) {
                                                            updateQuantity(item.ticket.id, value)
                                                        }
                                                    }}
                                                    sx={{
                                                        height: '32px',
                                                        '&.MuiFormControl-root.MuiTextField-root': {
                                                            border: 'none!important',
                                                            background: 'transparent',
                                                            '& .MuiInputBase-root': {
                                                                background: 'transparent!important',
                                                                '& .MuiInputBase-input': {
                                                                    background: 'transparent',
                                                                    transform: 'none!important',
                                                                    textAlign: 'center',
                                                                    color: 'primary.main',
                                                                    fontWeight: 600
                                                                }
                                                            }
                                                        }
                                                    }}
                                                />
                                            </Stack>
                                            <Stack>
                                                <Stack
                                                    onClick={() => updateQuantity(item.ticket.id, item.quantity + 1)}
                                                    sx={{
                                                        height: '32px',
                                                        width: '21px',
                                                        padding: 0,
                                                        boxSizing: 'border-box',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}>
                                                    <PlusIcon />
                                                </Stack>
                                            </Stack>
                                        </Stack>
                                    )}
                                </Stack>
                            </Stack>
                        </Stack>
                        {index < cartItems.length - 1 && <Divider sx={{ marginTop: '16px', marginBottom: '16px' }} />}
                    </Fragment>
                ))}
            </Stack>

            {/* Checkout Section */}
            <Stack
                sx={{
                    bgcolor: 'background.paper',
                    boxSizing: 'border-box',
                    borderRadius: '5px',
                    backgroundColor: 'warning.dark',
                    justifyContent: 'space-between',
                    flexDirection: 'row',
                    alignItems: 'center',
                    p: '16px 24px'
                }}>
                <Typography variant="h2" color="primary.main">
                    {translate('total')}
                </Typography>
                <Typography variant="h5" fontWeight={700} color="primary.main">
                    {formatPrice(cartTotal, currency)}
                </Typography>
            </Stack>
        </Stack>
    )
}

export default Cart
